/* eslint-disable react/no-unknown-property */
'use client';

import { createContext, ReactNode } from 'react';
import { UserBackendContextType as ImportedUserBackendContextType } from '@/types/context';
import { UserDTO } from '@/types/other';
import useUserQuery from '@/hooks/useUserQuery';
import { UseQueryResult } from '@tanstack/react-query';

// Define a type that omits 'data' from UseQueryResult and adds the specific 'data' type we want
type CustomUserQueryResult = Omit<UseQueryResult<UserDTO | null>, 'data'> & {
  data: UserDTO | null;
  refetchDone: boolean;
};

// Define the actual shape of the context value
export interface UserBackendContextValue extends CustomUserQueryResult {
  refetchUser: () => void;
}

// Initialize with dummy refetchUser
const initUserBackend: UserBackendContextValue = {
  data: null,
  error: null,
  isError: false,
  isLoading: true,
  isSuccess: false,
  isFetching: false,
  isPending: true, // 'pending' is usually the initial status before first fetch attempt
  status: 'pending',
  refetch: () => Promise.resolve({} as any), // Placeholder refetch from UseQueryResult
  refetchDone: false,
  refetchUser: () => {}, // Dummy function for initial value
} as UserBackendContextValue; // Simpler assertion now

// Use the specific context value type
export const UserBackendContext = createContext<UserBackendContextValue>(initUserBackend);

export const UserBackendProvider = ({
  children,
  userSSR,
  isStaticPage,
}: {
  children: ReactNode;
  userSSR: UserDTO | null;
  isStaticPage: boolean;
}) => {
  const userQueryData = useUserQuery(userSSR, isStaticPage);

  // Construct the value including the actual refetch function
  const contextValue: UserBackendContextValue = {
    ...userQueryData,
    data: userQueryData.data ?? null, // Ensure data is UserDTO or null, not undefined
    refetchUser: () => userQueryData.refetch(), // Map refetch to refetchUser
  };

  return (
    // Provide the correctly shaped value
    <UserBackendContext.Provider value={contextValue}>
      {userQueryData.data && ( // Check userQueryData instead of data for the style block logic
        <style jsx global>
          {`
            .needsclick {
              display: none !important;
            }
          `}
        </style>
      )}
      {children}
    </UserBackendContext.Provider>
  );
};
